import type { IGetActionsData } from "@api";
import type { ICandidate } from "@bbj-types/candidate";
import type { ISocialOffer } from "@bbj-types/offer";
import type { ISocialApplyTransition } from "@bbj-types/socialApply";
import { SocialApplyStatus } from "@bbj-types/socialApply/enum";
import { SendSocialOfferModal } from "@components/socialOffer/SendSocialOfferModal";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import { Box, Button, ClickAwayListener, Divider, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { isInternalOfferSource } from "@root/utils/enums/offer";
import { CascadingMenu, type ICascadingMenuProps } from "@surfaces/CascadingMenu";
import { useState } from "react";
import { ChallengesMenu } from "./ChallengesMenu";
import { useFollowUpSocialOffer } from "./logics";
import { FollowUpMenuPopper, classes } from "./styles";

export interface IFollowUpSocialOfferProps<T extends ISocialOffer> {
  offer: T & {
    status?: SocialApplyStatus;
    transitions?: ISocialApplyTransition[];
    socialApplyId?: string;
  };
  onUpdateOffer?: (
    socialOffer: T & { status?: SocialApplyStatus; transitions?: ISocialApplyTransition[]; socialApplyId?: string },
    generatedAction?: IGetActionsData
  ) => void;
  candidate: ICandidate;
  expired: boolean;
  displayFup?: boolean;
  setOfferFupModal?: (
    offer: T & {
      applyCreatedAt?: Date;
      status?: SocialApplyStatus;
      transitions?: ISocialApplyTransition[];
      socialApplyId?: string;
    }
  ) => void;
}

export const FollowUpSocialOfferV2 = <T extends ISocialOffer>({
  offer,
  onUpdateOffer,
  candidate,
  expired,
  displayFup,
  setOfferFupModal,
}: IFollowUpSocialOfferProps<T>) => {
  const {
    matchingChallenges,
    anchorEl,
    setAnchorEl,
    handleProposeSocialOffer,
    loadingPropose,
    candidateAgreedCommunication,
  } = useFollowUpSocialOffer(offer, onUpdateOffer, candidate);
  const open = Boolean(anchorEl);
  const [sendModalOpen, setSendModalOpen] = useState(false);

  const showProposeBySmsEmail =
    !offer.status ||
    [
      SocialApplyStatus.PROPOSED,
      SocialApplyStatus.INTERESTED_CANDIDATE,
      SocialApplyStatus.PRESCRIPTION_FILLED,
    ].includes(offer.status);

  const showPrescription =
    displayFup &&
    offer.provider?.enableFup &&
    (!offer.status || [SocialApplyStatus.PROPOSED, SocialApplyStatus.INTERESTED_CANDIDATE].includes(offer.status)) &&
    isInternalOfferSource(offer.source);

  const menuItems: ICascadingMenuProps["items"] = [
    {
      label: "Proposée par un autre canal",
      disabled: false,
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.PROPOSED, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1 && !offer.status
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.PROPOSED}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.PROPOSED}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Candidat intéressé",
      disabled: offer.status === SocialApplyStatus.INTERESTED_CANDIDATE,
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.INTERESTED_CANDIDATE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1 && !offer.status
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.INTERESTED_CANDIDATE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.INTERESTED_CANDIDATE}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Prescription réalisée",
      disabled: offer.status === SocialApplyStatus.PRESCRIPTION_FILLED,
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.PRESCRIPTION_FILLED, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1 && !offer.status
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.PRESCRIPTION_FILLED}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.PRESCRIPTION_FILLED}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Candidat intégré",
      disabled: offer.status === SocialApplyStatus.INTEGRATED_CANDIDATE,
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.INTEGRATED_CANDIDATE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1 && !offer.status
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.INTEGRATED_CANDIDATE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.INTEGRATED_CANDIDATE}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Refus par la structure",
      disabled: offer.status === SocialApplyStatus.REFUSED_STRUCTURE,
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.REFUSED_STRUCTURE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1 && !offer.status
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.REFUSED_STRUCTURE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.REFUSED_STRUCTURE}
              />
            ),
          }
        : undefined),
    },
    {
      label: "Refus par le candidat",
      disabled: offer.status === SocialApplyStatus.REFUSED_CANDIDATE,
      onConfirm: () => {
        handleProposeSocialOffer(SocialApplyStatus.REFUSED_CANDIDATE, matchingChallenges);
        setAnchorEl(null);
      },
      ...(matchingChallenges.length > 1 && !offer.status
        ? {
            component: () => (
              <ChallengesMenu
                key={SocialApplyStatus.REFUSED_CANDIDATE}
                onConfirm={() => {
                  setAnchorEl(null);
                }}
                onCancel={() => {
                  setAnchorEl(null);
                }}
                matchingChallenges={matchingChallenges}
                handleProposeSocialOffer={handleProposeSocialOffer}
                status={SocialApplyStatus.REFUSED_CANDIDATE}
              />
            ),
          }
        : undefined),
    },
  ];

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  return (
    <ClickAwayListener
      onClickAway={(e) => {
        e.stopPropagation();
        setAnchorEl(null);
      }}
    >
      <Box onClick={(e) => e.stopPropagation()}>
        <Button
          variant="outlined"
          size="small"
          endIcon={open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
          onClick={handleClick}
          disabled={expired || loadingPropose}
          sx={{ zIndex: 1 }}
        >
          actions
        </Button>
        <Menu
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorEl={anchorEl}
          slotProps={{
            paper: {
              elevation: 0,
              variant: "outlined",
              sx: (theme) => ({
                borderColor: theme.palette.border.main,
                filter: "unset",
              }),
            },
          }}
        >
          <FollowUpMenuPopper
            className={classes.root}
            anchorEl={anchorEl}
            placement="bottom-end"
            open={Boolean(anchorEl)}
          >
            {showProposeBySmsEmail && (
              <Tooltip
                title="Le candidat n'a pas accepté d'être contacté par SMS ou email"
                disableHoverListener={candidateAgreedCommunication}
              >
                <Box>
                  <MenuItem
                    sx={{ pb: 0.5, pt: 1 }}
                    disabled={!candidateAgreedCommunication}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setSendModalOpen(true);
                      setAnchorEl(null);
                    }}
                  >
                    <Typography variant="body1">Proposer par SMS ou email au candidat</Typography>
                  </MenuItem>
                </Box>
              </Tooltip>
            )}
            {showPrescription && (
              <Box>
                <MenuItem
                  sx={{ py: 0.5 }}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOfferFupModal?.(offer);
                    setAnchorEl(null);
                  }}
                >
                  <Typography variant="body1">Réaliser une prescription</Typography>
                </MenuItem>
              </Box>
            )}
            {showProposeBySmsEmail || showPrescription ? (
              <>
                <Divider />
                <Box sx={{ px: 2, pt: 1, pointerEvents: "none" }}>
                  <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                    Ou indiquer manuellement l'offre comme...{" "}
                  </Typography>
                </Box>
              </>
            ) : (
              <Box sx={{ px: 2, pt: 1, pointerEvents: "none" }}>
                <Typography variant="body1" sx={{ fontStyle: "italic" }}>
                  Indiquer manuellement l'offre comme...{" "}
                </Typography>
              </Box>
            )}
            <CascadingMenu
              items={menuItems}
              itemStyles={{
                sx: {
                  pl: 4,
                  "& .MuiTypography-root": {
                    marginLeft: 0,
                  },
                },
              }}
            />
          </FollowUpMenuPopper>
        </Menu>
        {sendModalOpen && (
          <SendSocialOfferModal
            open={sendModalOpen}
            onClose={() => setSendModalOpen(false)}
            offer={offer}
            candidate={candidate}
            challenges={matchingChallenges}
            onSuccess={(socialApply, action) => {
              if (onUpdateOffer) {
                onUpdateOffer(
                  {
                    ...offer,
                    status: socialApply.status as SocialApplyStatus,
                    transitions: socialApply.transitions,
                    socialApplyId: socialApply._id,
                  },
                  action
                );
              }
            }}
          />
        )}
      </Box>
    </ClickAwayListener>
  );
};
